import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Plus, Minus, Package, Loader2 } from 'lucide-react';
import { inventoryApi } from '@/api/inventoryApi';
import { handleApiError } from '@/api/client';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import type { ShelfInventory } from '@/types/api';

interface StockAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  item: ShelfInventory | null;
  type: 'add' | 'remove' | 'count';
}

export function StockAdjustmentModal({ isOpen, onClose, onSuccess, item, type }: StockAdjustmentModalProps) {
  const queryClient = useQueryClient();
  const [quantity, setQuantity] = useState('1');
  const [notes, setNotes] = useState('');
  const [binLocation, setBinLocation] = useState('');
  const [binNumber, setBinNumber] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !item) return;
    setQuantity(type === 'count' ? String(item.quantityOnShelf) : '1');
    setNotes(item.notes || '');
    setBinLocation(item.binLocation || '');
    setBinNumber(item.binNumber || '');
    setError('');
  }, [isOpen, item, type]);

  const adjustmentMutation = useMutation({
    mutationFn: ({ item, quantity, notes, binLocation, binNumber }: { item: ShelfInventory; quantity: number; notes?: string; binLocation?: string | null; binNumber?: string | null }) => {
      if (type === 'count') {
        return inventoryApi.adjust({
          productVariantId: item.productVariantId,
          locationId: item.locationId,
          quantity,
          notes,
          binLocation,
          binNumber,
        });
      }
      return type === 'add'
        ? inventoryApi.addStock(item.id, quantity, notes)
        : inventoryApi.removeStock(item.id, quantity, notes);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['low-stock'] });
      onSuccess?.();
      handleClose();
    },
    onError: (err: any) => {
      setError(handleApiError(err));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!item) return;

    const qty = parseInt(quantity);
    if (isNaN(qty) || qty < 0 || (type !== 'count' && qty < 1)) {
      setError(type === 'count' ? 'Count cannot be negative' : 'Quantity must be at least 1');
      return;
    }

    if (type === 'remove' && qty > item.availableQuantity) {
      setError(`Cannot remove more than available (${item.availableQuantity})`);
      return;
    }

    adjustmentMutation.mutate({
      item,
      quantity: qty,
      notes: notes.trim() || undefined,
      binLocation: binLocation.trim() || null,
      binNumber: binNumber.trim() || null,
    });
  };

  const handleClose = () => {
    setQuantity('1');
    setNotes('');
    setBinLocation('');
    setBinNumber('');
    setError('');
    onClose();
  };

  if (!isOpen || !item) return null;

  const isAdd = type === 'add';
  const isCount = type === 'count';
  const title = isCount ? 'Take Inventory' : isAdd ? 'Add Stock' : 'Remove Stock';
  const Icon = isCount ? Package : isAdd ? Plus : Minus;
  const colorClass = isCount ? 'text-blue-600 dark:text-blue-400' : isAdd ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
  const bgClass = isCount ? 'bg-blue-500/10' : isAdd ? 'bg-green-500/10' : 'bg-red-500/10';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/60 dark:bg-charcoal-950/80 backdrop-blur-sm" onClick={handleClose} />
      
      <div className="relative w-full max-w-md card animate-scale-in">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-charcoal-700">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${bgClass} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${colorClass}`} />
            </div>
            <div>
              <h2 className="font-sans text-xl font-semibold text-gray-900 dark:text-warm-cream">{title}</h2>
              <p className="text-sm text-gray-600 dark:text-warm-sand">{item.variantSku}</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 rounded-lg">
            <X className="w-5 h-5 text-gray-600 dark:text-warm-sand" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <ErrorAlert message={error} />
          )}

          <div className="p-4 bg-white dark:bg-charcoal-800/50 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <Package className="w-5 h-5 text-gray-600 dark:text-warm-sand" />
              <span className="text-gray-900 dark:text-warm-cream font-medium">{item.variantSku}</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-600 dark:text-warm-sand">On Shelf</p>
                <p className="text-gray-900 dark:text-warm-cream font-medium">{item.quantityOnShelf}</p>
              </div>
              <div>
                <p className="text-gray-600 dark:text-warm-sand">Reserved</p>
                <p className="text-gray-900 dark:text-warm-cream font-medium">{item.quantityReserved}</p>
              </div>
              <div>
                <p className="text-gray-600 dark:text-warm-sand">Available</p>
                <p className="text-green-600 dark:text-green-400 font-medium">{item.availableQuantity}</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
              {isCount ? 'Counted Quantity' : `Quantity to ${isAdd ? 'Add' : 'Remove'}`} <span className="text-red-600 dark:text-red-400">*</span>
            </label>
            <input
              type="number"
              min={isCount ? '0' : '1'}
              max={!isAdd && !isCount ? item.availableQuantity : undefined}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-gray-900 dark:text-warm-cream focus:border-amber-450 focus:outline-none"
              autoFocus
            />
            {isCount ? (
              <p className="mt-1 text-xs text-gray-600 dark:text-warm-sand">
                Enter the actual count on the shelf for this location.
              </p>
            ) : !isAdd && (
              <p className="mt-1 text-xs text-gray-600 dark:text-warm-sand">
                Maximum: {item.availableQuantity} available
              </p>
            )}
          </div>

          {isCount && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                  Bin Location
                </label>
                <input
                  type="text"
                  value={binLocation}
                  onChange={(e) => setBinLocation(e.target.value)}
                  placeholder="Studio A, Patio, 7 Ave Store..."
                  className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-gray-900 dark:text-warm-cream focus:border-amber-450 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                  Bin Number
                </label>
                <input
                  type="text"
                  value={binNumber}
                  onChange={(e) => setBinNumber(e.target.value)}
                  placeholder="Bin 1, A-3, Shelf 2..."
                  className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-gray-900 dark:text-warm-cream focus:border-amber-450 focus:outline-none"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={isCount ? 'Remarks from the count...' : `Why are you ${isAdd ? 'adding' : 'removing'} stock?`}
              rows={2}
              className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-gray-900 dark:text-warm-cream focus:border-amber-450 focus:outline-none resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-charcoal-700">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary"
              disabled={adjustmentMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`btn-primary ${!isAdd && !isCount ? 'bg-red-500 hover:bg-red-600' : ''}`}
              disabled={adjustmentMutation.isPending}
            >
              {adjustmentMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isCount ? 'Saving Count...' : isAdd ? 'Adding...' : 'Removing...'}
                </>
              ) : (
                <>
                  <Icon className="w-4 h-4 mr-2" />
                  {isCount ? 'Save Count' : isAdd ? 'Add Stock' : 'Remove Stock'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
